import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/ui/base/button';
import { Checkbox } from '@/ui/base/checkbox';
import { PageBanner } from '@/ui/PageBanner';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar } from '@/ui/FilterBar';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';
import { TimelineBadgeStepper } from '@/ui/TimelineBadgeStepper';
import { UpdatedAt } from '@/ui/UpdatedAt';
import { Badge } from '@/ui/base/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import {
  AlertTriangle,
  Banknote,
  Bell,
  CalendarCheck,
  CheckCircle2,
  Clock3,
  Send,
  TriangleAlert,
} from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';

import { CicloMensalAbas } from '../ciclo-mensal-sme/CicloMensalAbas';
import { useFechamentoV2Lista } from '../hooks/useFechamentoV2';
import type { EtapaFechamentoV2, FechamentoV2ListaItem } from '../types';
import { ETAPA_META, ETAPAS_ORDEM, construirEtapasTimeline } from './etapas';
import { NaturezaToggle } from './NaturezaToggle';
import { useFechamentoBasePath } from './useFechamentoBasePath';

const ETAPAS_FILTRO: EtapaFechamentoV2[] = [
  'abertura',
  'apontamento',
  'validacao',
  'processamento',
  'pagamento',
  'concluido',
];

/**
 * `/operacao/financeiro/fechamento-mensal` — listagem de Fechamento Mensal V2:
 * uma linha por competência × natureza (não por DRE, que é a visão antiga da
 * V1 — `FechamentoMensalPage.tsx`, mantida no módulo mas fora de uso a partir
 * desta rota, por decisão do dono do produto).
 */
/** AAAA-MM do mês anterior ao atual — competência-alvo por padrão (o mês
 * corrente ainda está em apontamento, o fechamento trabalha o mês fechado). */
function mesPadraoEntrada(): string {
  const hoje = new Date();
  hoje.setDate(1);
  hoje.setMonth(hoje.getMonth() - 1);
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
}

const anoAtual = new Date().getFullYear();
const ANOS_FILTRO = Array.from({ length: 4 }, (_, i) => anoAtual - i);

/**
 * Filtros na URL, não em estado local — deep-link e bookmark precisam
 * reabrir a mesma vista (`useUrlFilters.ts`). Confere com a referência
 * (`FechamentoMensalV2ListPage.tsx` do protótipo, `hasIncomingFilters`):
 * as mesmas sete chaves decidem se o redirecionamento pra competência-alvo
 * é pulado. `dres` e `meus` entram só nessa checagem — não têm filtro
 * próprio nesta tela, mas um link com essas chaves não deve ser jogado fora
 * pelo redirecionamento automático.
 */
const CHAVES_DEEP_LINK = ['ano', 'natureza', 'etapa', 'atrasados', 'divergencia', 'dres', 'meus'];
const PADROES_FILTRO = {
  ano: String(anoAtual),
  natureza: 'PF',
  etapa: '',
  atrasados: '',
  divergencia: '',
};

/** Ação em lote só vale pras competências na etapa elegível — mesma matriz
 * do protótipo (`configuracoes.fechamento_v2_bulk_matrix`, aqui fixa por não
 * termos tela de parametrização deste módulo ainda).
 *
 * A barra aparece para todas as personas, como no protótipo, mas nenhuma ação
 * executa: não há rota em `apps/api/src/modulos/` que notifique, exporte ou
 * escalone. O clique mostra o aviso de indisponibilidade e a seleção fica como
 * está — limpar a seleção ou contar "processadas" seria fingir sucesso. */
type AcaoLote = 'notificar_sme' | 'exportar_sof' | 'escalonar_dre';
const LOTE_ELEGIVEL: Record<AcaoLote, EtapaFechamentoV2> = {
  notificar_sme: 'validacao',
  exportar_sof: 'processamento',
  escalonar_dre: 'apontamento',
};
const LOTE_ROTULO: Record<AcaoLote, string> = {
  notificar_sme: 'Notificar SME',
  exportar_sof: 'Exportar lote SOF',
  escalonar_dre: 'Escalonar cobrança DRE',
};
const LOTE_ICONE: Record<AcaoLote, typeof Send> = {
  notificar_sme: Bell,
  exportar_sof: Send,
  escalonar_dre: TriangleAlert,
};

export function FechamentoMensalV2ListPage() {
  const navigate = useNavigate();
  const base = useFechamentoBasePath();
  const [parametros] = useSearchParams();
  const { values: filtros, setFilter, setMany } = useUrlFilters(PADROES_FILTRO);
  const ano = Number(filtros.ano) || anoAtual;
  const natureza: 'PF' | 'PJ' = filtros.natureza === 'PJ' ? 'PJ' : 'PF';
  const etapa = (filtros.etapa || 'TODAS') as EtapaFechamentoV2 | 'TODAS';
  const atrasados = (filtros.atrasados || 'TODOS') as 'TODOS' | 'SIM' | 'NAO';
  const divergencia = (filtros.divergencia || 'TODOS') as 'TODOS' | 'SIM' | 'NAO';
  const hasIncomingFilters = useMemo(
    () => CHAVES_DEEP_LINK.some((chave) => parametros.has(chave)),
    [parametros],
  );
  const [selecionados, definirSelecionados] = useState<string[]>([]);

  // Entrada padrão: cai direto no detalhe da competência-alvo (mês anterior),
  // igual ao protótipo. A lista fica como fallback, acessível pelo link "Ver
  // lista de competências" no cabeçalho do detalhe (`?view=lista`). Os hooks
  // abaixo rodam sempre (regra dos hooks) — o redirecionamento acontece só no
  // retorno, depois de todos declarados.
  const modoFallback = parametros.get('view') === 'lista';
  const deveRedirecionar = !modoFallback && !hasIncomingFilters;

  const { data, isLoading, isError, refetch, dataUpdatedAt, isFetching } = useFechamentoV2Lista({});

  // Universo do ano+natureza selecionados — base dos KPIs, sem os filtros
  // operacionais (etapa/atrasados/divergência), pra os números do topo não
  // oscilarem conforme o usuário refina a lista abaixo.
  const itensAno = useMemo(
    () =>
      (data ?? []).filter(
        (i) => i.natureza === natureza && i.mesReferencia.slice(0, 4) === String(ano),
      ),
    [data, natureza, ano],
  );

  const itens = useMemo(
    () =>
      itensAno.filter((i) => {
        if (etapa !== 'TODAS' && i.etapa !== etapa) return false;
        if (atrasados === 'SIM' && !i.atrasado) return false;
        if (atrasados === 'NAO' && i.atrasado) return false;
        if (divergencia === 'SIM' && !i.divergencia) return false;
        if (divergencia === 'NAO' && i.divergencia) return false;
        return true;
      }),
    [itensAno, etapa, atrasados, divergencia],
  );

  const kpis = useMemo(() => {
    const base = itensAno;
    const abertas = base.filter((i) => i.etapa !== 'concluido').length;
    const atrasadas = base.filter((i) => i.atrasado).length;
    const aprovadas = base.filter(
      (i) => i.etapa === 'processamento' || i.etapa === 'pagamento' || i.etapa === 'concluido',
    ).length;
    const pagas = base.filter((i) => i.etapa === 'concluido').length;
    const totalPago = base.reduce((s, i) => s + parseFloat(i.valorPago), 0);
    const totalCalc = base.reduce((s, i) => s + parseFloat(i.valorCalculado), 0);
    return { total: base.length, abertas, atrasadas, aprovadas, pagas, totalPago, totalCalc };
  }, [itensAno]);

  if (deveRedirecionar) {
    const mes = mesPadraoEntrada();
    return <Navigate to={`${base}/fechamento-mensal/${mes}-01__PF`} replace />;
  }

  const fmtBRL = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  const stats: StatItem[] = [
    {
      title: 'Abertas',
      value: kpis.abertas,
      icon: Clock3,
      description: `${kpis.total} competências`,
    },
    {
      title: 'Atrasadas',
      value: kpis.atrasadas,
      icon: AlertTriangle,
      description: kpis.atrasadas > 0 ? 'Risco de SLA' : 'No prazo',
      className: kpis.atrasadas > 0 ? 'border-warning/40' : undefined,
    },
    { title: 'Aprovadas', value: kpis.aprovadas, icon: CheckCircle2 },
    { title: 'Pagas', value: kpis.pagas, icon: Banknote },
    {
      title: 'Pago / Calculado',
      value: `${fmtBRL(kpis.totalPago)} / ${fmtBRL(kpis.totalCalc)}`,
      icon: Banknote,
    },
  ];

  const selecionadosItens = itens.filter((i) => selecionados.includes(i.id));

  function alternarTodosVisiveis() {
    const idsVisiveis = itens.map((i) => i.id);
    const todosSelecionados =
      idsVisiveis.length > 0 && idsVisiveis.every((id) => selecionados.includes(id));
    definirSelecionados((atual) =>
      todosSelecionados
        ? atual.filter((id) => !idsVisiveis.includes(id))
        : [...new Set([...atual, ...idsVisiveis])],
    );
  }

  function executarLote() {
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  const colunas: DataTableColumn<FechamentoV2ListaItem>[] = [
    {
      id: 'selecao',
      header: '',
      isSelectionColumn: true,
      headerClassName: 'w-10',
      cellClassName: 'w-10',
      cell: (i) => (
        <Checkbox
          checked={selecionados.includes(i.id)}
          onCheckedChange={() =>
            definirSelecionados((atual) =>
              atual.includes(i.id) ? atual.filter((id) => id !== i.id) : [...atual, i.id],
            )
          }
          aria-label={`Selecionar ${i.mesReferencia} ${i.natureza}`}
        />
      ),
    },
    {
      header: 'Competência',
      sortKey: (i) => i.mesReferencia,
      cell: (i) => (
        <div>
          <p className="font-medium capitalize">
            {new Date(`${i.mesReferencia}T00:00:00`).toLocaleDateString('pt-BR', {
              month: 'long',
              year: 'numeric',
            })}
          </p>
          <p className="text-xs text-muted-foreground">
            {i.natureza === 'PF' ? 'Pessoa Física' : 'PJ / Cooperativa'}
          </p>
        </div>
      ),
    },
    {
      header: 'Etapa',
      sortKey: (i) => ETAPAS_ORDEM.indexOf(i.etapa),
      cell: (i) => (
        <TimelineBadgeStepper
          steps={construirEtapasTimeline(
            i.etapa,
            i.responsavel,
            i.proximoPasso,
            i.atrasado,
            i.diasParaPrazo,
          )}
        />
      ),
    },
    { header: 'Próximo passo', cell: (i) => <span className="text-sm">{i.proximoPasso}</span> },
    {
      header: 'Resp.',
      sortKey: (i) => i.responsavel,
      cell: (i) => <Badge variant="outline">{i.responsavel}</Badge>,
    },
    {
      header: 'SLA',
      tooltip: 'Dias até o prazo de fechamento desta competência.',
      sortKey: (i) => i.diasParaPrazo,
      cell: (i) =>
        i.etapa === 'concluido' ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : i.atrasado ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            {Math.abs(i.diasParaPrazo)}d atraso
          </span>
        ) : (
          <span className="text-xs tabular-nums text-muted-foreground">em {i.diasParaPrazo}d</span>
        ),
    },
    {
      header: 'OS pagas',
      sortKey: (i) => (i.totalOs > 0 ? i.totalOsPagas / i.totalOs : 0),
      cell: (i) => {
        const pct = i.totalOs > 0 ? Math.round((i.totalOsPagas / i.totalOs) * 100) : 0;
        return (
          <span className="tabular-nums text-xs">
            <strong>{i.totalOsPagas}</strong>/{i.totalOs} ({pct}%)
          </span>
        );
      },
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Valor pago / calc.',
      sortKey: (i) => parseFloat(i.valorPago),
      cell: (i) => (
        <div className="text-xs tabular-nums">
          <div className="font-medium">
            <MoneyDisplay value={i.valorPago} />
          </div>
          <div className={i.divergencia ? 'text-warning' : 'text-muted-foreground'}>
            de <MoneyDisplay value={i.valorCalculado} />
          </div>
        </div>
      ),
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fechamento mensal"
        description="Cada competência abre automaticamente no dia 1º. As DREs preenchem o apontamento, a SME valida e o DTP processa e paga."
      >
        <Select value={String(ano)} onValueChange={(v) => setFilter('ano', v)}>
          <SelectTrigger className="w-[110px]" aria-label="Ano">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ANOS_FILTRO.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <NaturezaToggle value={natureza} onChange={(v) => setFilter('natureza', v)} />
        <UpdatedAt
          updatedAt={dataUpdatedAt}
          isFetching={isFetching}
          onRefresh={() => void refetch()}
        />
      </PageHeader>

      {modoFallback && (
        <PageBanner
          tone="info"
          icon={CalendarCheck}
          title="Visualização de lista (fallback)"
          description="A entrada padrão de Fechamento Mensal abre direto na competência alvo. Use esta lista para navegar entre múltiplas competências."
        />
      )}

      {/* Abas do Ciclo mensal só na casca de SME, abaixo dos avisos do cabeçalho
          — no protótipo, o `#ciclo-mensal-tabs-slot` desta lista. O DTP não as
          tem: lá Medições e Demonstrativos são itens próprios do menu. */}
      {base === '/sme' && <CicloMensalAbas />}

      {itensAno.length > 0 && (
        <StatsGrid stats={stats} columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" />
      )}

      <FilterBar
        fields={[
          {
            key: 'etapa',
            label: 'Etapa',
            type: 'select',
            width: 'w-full sm:w-[200px]',
            allOptionLabel: 'Todas as etapas',
            options: ETAPAS_FILTRO.map((e) => ({ value: e, label: ETAPA_META[e].label })),
          },
          {
            key: 'atrasados',
            label: 'Atrasados',
            type: 'select',
            width: 'w-full sm:w-[160px]',
            allOptionLabel: 'Todos',
            options: [
              { value: 'SIM', label: 'Só atrasados' },
              { value: 'NAO', label: 'No prazo' },
            ],
          },
          {
            key: 'divergencia',
            label: 'Divergência',
            type: 'select',
            width: 'w-full sm:w-[170px]',
            allOptionLabel: 'Todos',
            options: [
              { value: 'SIM', label: 'Com divergência' },
              { value: 'NAO', label: 'Sem divergência' },
            ],
          },
        ]}
        values={{
          etapa: etapa === 'TODAS' ? '' : etapa,
          atrasados: atrasados === 'TODOS' ? '' : atrasados,
          divergencia: divergencia === 'TODOS' ? '' : divergencia,
        }}
        onChange={(chave, valor) => setFilter(chave, valor)}
        onClear={() => setMany({ etapa: '', atrasados: '', divergencia: '' })}
      />

      {selecionados.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2">
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={itens.length > 0 && itens.every((i) => selecionados.includes(i.id))}
              onCheckedChange={alternarTodosVisiveis}
              aria-label="Selecionar todas visíveis"
            />
            <span className="font-medium text-foreground">
              {selecionados.length} competência(s) selecionada(s)
            </span>
            <Button size="sm" variant="ghost" onClick={() => definirSelecionados([])}>
              Limpar
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(LOTE_ROTULO) as AcaoLote[]).map((acao) => {
              const Icone = LOTE_ICONE[acao];
              const elegiveis = selecionadosItens.filter(
                (i) => i.etapa === LOTE_ELEGIVEL[acao],
              ).length;
              return (
                <Button
                  key={acao}
                  size="sm"
                  variant="outline"
                  disabled={elegiveis === 0}
                  onClick={executarLote}
                >
                  <Icone className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                  {`${LOTE_ROTULO[acao]} (${elegiveis})`}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={colunas}
          data={itens}
          isLoading={isLoading}
          selectedIds={selecionados}
          onTogglePageSelection={alternarTodosVisiveis}
          onRowClick={(i) =>
            navigate(
              `${base}/fechamento-mensal/${i.id}?mes=${i.mesReferencia.slice(0, 7)}&tipo=${i.natureza}`,
            )
          }
          emptyTitle="Nenhuma competência encontrada"
          emptyDescription="Ajuste o filtro de etapa."
          ariaLabel="Fechamento mensal por competência"
        />
      )}
    </div>
  );
}

export default FechamentoMensalV2ListPage;
